import { BillingKeys, AccountKeys } from '../Keys'
import { useCreate, useDelete, useGetList, useGetOne, usePatch } from '../request'

import type {
  CheckoutStripeRequest,
  UpgradeStripeRequest,
  Subscription,
  UnsubscribeQuestion,
  UnsubscribeQuestionAnswer,
  SetupIntentResponse,
  CompleteSetupIntentRequest,
  CompleteSetupIntentResponse,
  BillingPortalResponse,
} from '@models/Billing'
import { useQueryClient } from '@tanstack/react-query'

const PATHS = {
  stripeCheckout: 'api/stripe/checkout',
  stripeSetupIntent: 'api/stripe/setup-intent',
  stripeSetupIntentComplete: 'api/stripe/setup-intent/complete',
  stripePortal: 'api/stripe/portal',
  subscription: 'api/teams/subscription',
  unsubscribeQuestion: 'api/teams/unsubscribe-questions',
  unsubscribeQuestionAnswer: 'api/teams/unsubscribe-questionnaire',
}
export const useGetTeamSubscription = ({ enabled = true, suspense = false } = {}) => {
  return useGetOne<Subscription>([BillingKeys.Subscription], PATHS.subscription, {
    query: {
      cacheTime: Infinity,
      staleTime: Infinity,
      suspense,
      enabled,
    },
  })
}

export const useCheckoutStripe = () => {
  return useCreate<CheckoutStripeRequest, { checkout_url: string }>(PATHS.stripeCheckout, {
    mutation: { mutationKey: [BillingKeys.StripeCheckout] },
  })
}

export const useUpgradeStripe = () => {
  const cache = useQueryClient()
  return usePatch<UpgradeStripeRequest, { checkout_url: string }>(PATHS.subscription, {
    mutation: {
      mutationKey: [BillingKeys.StripeCheckout],
      onSuccess: () => cache.invalidateQueries([BillingKeys.Subscription]),
    },
  })
}

// Onboarding card capture (Stripe SetupIntent — saves a card without charging).
// `create` opens the intent + returns the client secret; `complete` attaches the
// confirmed PaymentMethod and invalidates the subscription so has_payment_method
// refreshes.
export const useCreateSetupIntent = () => {
  return useCreate<Record<string, never>, SetupIntentResponse>(PATHS.stripeSetupIntent, {
    mutation: { mutationKey: [BillingKeys.SetupIntent] },
  })
}

export const useCompleteSetupIntent = () => {
  const cache = useQueryClient()
  return useCreate<CompleteSetupIntentRequest, CompleteSetupIntentResponse>(PATHS.stripeSetupIntentComplete, {
    mutation: {
      mutationKey: [BillingKeys.SetupIntent],
      onSuccess: () => cache.invalidateQueries([BillingKeys.Subscription]),
    },
  })
}

// Stripe-hosted billing portal: update card, invoices, recover failed payments,
// cancel. Returns the URL to redirect to.
export const useCreateBillingPortal = () => {
  return useCreate<Record<string, never>, BillingPortalResponse>(PATHS.stripePortal, {
    mutation: { mutationKey: [BillingKeys.Portal] },
  })
}

export const useCancelSubscription = () => {
  const cache = useQueryClient()
  return useDelete(PATHS.subscription, {
    mutation: {
      mutationKey: [BillingKeys.CancelSubscription],
      onSuccess: () => {
        cache.invalidateQueries([BillingKeys.Subscription])
        cache.invalidateQueries([AccountKeys.Me])
      },
    },
  })
}

export const useGetUnsubscribeQuestion = () => {
  return useGetList<UnsubscribeQuestion>([BillingKeys.UnsubscribeQuestion], PATHS.unsubscribeQuestion, {
    query: {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  })
}

export const useSendUnsubscribeAnswer = () => {
  return useCreate<UnsubscribeQuestionAnswer>(PATHS.unsubscribeQuestionAnswer)
}
